import React, { FunctionComponent, useMemo } from 'react';
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMediaQuery } from 'react-responsive';
import { useSelector } from 'react-redux';

import { Article, Collection, RootStore } from '../app/types';
import logo from '../images/logo.svg';

export const ArticleHeader: FunctionComponent = () => {
    const navigate = useNavigate();
    const { articleID } = useParams();
    const articleIdNum = parseInt(articleID);
    const isMinWidth = useMediaQuery({ minWidth: 600 });

    const appName = useSelector((state: RootStore) => state.appName);
    const articles = useSelector((state: RootStore) => state.articles);
    const collections = useSelector((state: RootStore) => state.collections);
    const isLoggedIn = useSelector((state: RootStore) => state.loggedInUser !== null);

    const article = useMemo(() =>
        articles.find((a: Article) => a.ID === articleIdNum) ?? null,
        [articles, articleIdNum]
    );

    const collection = useMemo(() => {
        if (!article) return null;
        return collections.find((c: Collection) => c.ID === article.collectionID) ?? null;
    }, [collections, article]);

    const { byline, prevID, nextID } = useMemo(() => {
        if (!article) return { byline: "", prevID: 0, nextID: 0 };
        const collectionArticles = articles
            .filter((a: Article) => a.collectionID === article.collectionID)
            .sort((a, b) => a.orderIndex - b.orderIndex);
        const idx = collectionArticles.findIndex((a: Article) => a.ID === articleIdNum);
        return {
            byline: (idx + 1) + ' of ' + collectionArticles.length,
            prevID: idx > 0 ? collectionArticles[idx - 1].ID : 0,
            nextID: idx < (collectionArticles.length - 1) ? collectionArticles[idx + 1].ID : 0
        };
    }, [articles, article, articleIdNum]);

    const isEditor = window.location.pathname.indexOf("edit") > 0;

    const onCollectionNameClick = () => {
        if (isLoggedIn && collection) {
            navigate('/collection/' + collection.ID + '/edit');
        }
    };

    return (
        <header
            style={{
                backgroundColor: '#2d2d2d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                minHeight: 64,
            }}
        >
            {/* Left: Logo and App Name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                    src={logo}
                    alt="logo"
                    onClick={() => navigate('/')}
                    style={{
                        height: 48,
                        width: 48,
                        cursor: 'pointer',
                        borderRadius: 8,
                    }}
                />
                {isMinWidth && (
                    <Link
                        to="/"
                        style={{
                            color: '#e0e0e0',
                            fontSize: '1.25rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                        }}
                    >
                        {appName}
                    </Link>
                )}
            </div>

            {/* Right: Collection Info and Navigation */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <div
                    onClick={onCollectionNameClick}
                    style={{
                        color: '#e0e0e0',
                        fontSize: isMinWidth ? '1.25rem' : '1rem',
                        fontWeight: 500,
                        cursor: isLoggedIn ? 'pointer' : 'default',
                    }}
                >
                    {collection?.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.85rem' }}>
                    <span style={{ color: '#9e9e9e' }}>{byline}</span>
                    {prevID > 0 && (
                        <Link
                            to={"/article/" + prevID + (isEditor ? "/edit" : "")}
                            style={{ color: '#90caf9', textDecoration: 'none' }}
                        >
                            prev
                        </Link>
                    )}
                    {nextID > 0 && (
                        <Link
                            to={"/article/" + nextID + (isEditor ? "/edit" : "")}
                            style={{ color: '#90caf9', textDecoration: 'none' }}
                        >
                            next
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}
